import { useState, useEffect } from "react";
import styled from "styled-components";
import posthog from "posthog-js";

import { Button, color, font } from "@runwayhealth/runway-components-react";
import { Condition, ConditionType, Medication } from "@types-runway/condition";
import { medicationRequiresLabExam } from "@utils/labExam/labExam";
import { LabExamAcknowledgmentModal } from "@elements/LabExamAcknowledgmentModal";
import { ANALYTICS_EVENTS } from "@utils/analytics/events";
import useSidebarContent from "@hooks/useSidebarContent";

import { MedicationCard } from "./MedicationCard";

const defaultDescription = "Consult your provider for more details.";

type MedicationSelectorProps = {
  condition: Condition;
  conditionType: ConditionType;
  medications: Medication[];
  onClose: () => void;
  onConfirm: (medication: Medication) => void;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 0;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
`;

const Title = styled.h3`
  font-family: ${font.family};
  font-weight: 700;
  font-size: 1.25rem;
  margin: 0;
  color: ${color.brand.secondary.dark};
`;

const Subtitle = styled.p`
  font-family: ${font.family};
  font-weight: 600;
  font-size: 15px;
  color: ${color.brand.secondary.dark};
  margin: 0;
`;

const Description = styled.p`
  margin: 0;
  line-height: 1.5;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ConfirmWrapper = styled.div`
  padding-top: 16px;
`;

export const MedicationSelector = ({ condition, conditionType, medications, onClose, onConfirm }: MedicationSelectorProps) => {
  const hasMultipleMedications = medications.length > 1;
  const [selectedMedication, setSelectedMedication] = useState<Medication | null>(
    hasMultipleMedications ? null : condition.suggestedMedication ?? null
  );

  const { getTreatmentDescription, prefetchIndex } = useSidebarContent();
  // Triggers re-render when index is cached so getTreatmentDescription reads populated data
  const [, setIndexReady] = useState(false);

  useEffect(() => {
    prefetchIndex().then(() => setIndexReady(true));
    // prefetchIndex is stable in behavior (module-level cache), safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const conditionPrice = `$${Math.round((condition.price ?? 0) / 100)}`;

  const [labExamPopupIsOpen, setLabExamPopupIsOpen] = useState(false);

  const handleConfirm = () => {
    if (!selectedMedication) return;

    if (medicationRequiresLabExam(selectedMedication)) {
      posthog.capture(ANALYTICS_EVENTS.LAB_EXAM_REQUIREMENT_SHOWN, {
        source: "medication_selector",
        medication_name: selectedMedication.name,
        condition_name: condition.product.name,
      });
      setLabExamPopupIsOpen(true);
      return;
    }

    posthog.capture(ANALYTICS_EVENTS.MEDICATION_CONFIRMED, {
      medication_name: selectedMedication.name,
      condition_name: condition.product.name,
      requires_lab_exam: false,
    });
    onConfirm(selectedMedication);
    onClose();
  };

  const handleLabExamProceed = () => {
    if (!selectedMedication) return;

    posthog.capture(ANALYTICS_EVENTS.LAB_EXAM_REQUIREMENT_ACCEPTED, {
      source: "medication_selector",
      medication_name: selectedMedication.name,
      condition_name: condition.product.name,
    });
    posthog.capture(ANALYTICS_EVENTS.MEDICATION_CONFIRMED, {
      medication_name: selectedMedication.name,
      condition_name: condition.product.name,
      requires_lab_exam: true,
    });
    onConfirm(selectedMedication);
    setLabExamPopupIsOpen(false);
    onClose();
  };

  const handleLabExamCancel = () => {
    posthog.capture(ANALYTICS_EVENTS.LAB_EXAM_REQUIREMENT_DECLINED, {
      source: "medication_selector",
      medication_name: selectedMedication?.name,
      condition_name: condition.product.name,
    });
    setLabExamPopupIsOpen(false);
  };

  if (medications.length === 0) {
    return (
      <Container>
        <Title>{condition.product.name}</Title>
        <Description>No medication options available for this condition.</Description>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{condition.product.name}</Title>
        <Subtitle>Do you have a medication preference?</Subtitle>
        <Description>
          There are multiple prescription options available for {condition.product.name.toLowerCase()}{" "}
          prevention. Please select your preferred medication below. A licensed provider will review
          your choice for clinical appropriateness based on your health history and travel plans.
        </Description>
      </Header>
      <OptionsList role="listbox" aria-label={`Medications for ${condition.product.name}`}>
        {medications.map((medication) => (
          <MedicationCard
            key={medication.id ?? medication.name}
            medication={medication}
            price={conditionPrice}
            isSelected={selectedMedication?.name === medication.name}
            description={
              (medication.name ? getTreatmentDescription(conditionType, medication.name) : null)
                ?? defaultDescription
            }
            onSelect={() => {
              setSelectedMedication(medication);
            }}
          />
        ))}
      </OptionsList>
      <ConfirmWrapper>
        <Button
          data-testid="confirm-medication"
          onClick={handleConfirm}
          size="lg"
          isBlock
        >
          Confirm
        </Button>
      </ConfirmWrapper>
      <LabExamAcknowledgmentModal
        open={labExamPopupIsOpen}
        medicationName={selectedMedication?.name ?? "This medication"}
        onCancel={handleLabExamCancel}
        onProceed={handleLabExamProceed}
      />
    </Container>
  );
};
